import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const NAMES = { statement: "Statement email", reminder: "Payment reminder email" };
const SAMPLE = { customer: "Canton Oaks Nursing & Rehab", company: "American Scale Company", date: "11/3/2026", total: "$450.00", count: 3, oldest_days: 64, phone: "903-258-7947" };
const fill = (t) => t.replace(/\{(\w+)\}/g, (m, k) => (k in SAMPLE ? SAMPLE[k] : m));

function TemplateEditor({ kind, t, def, onChange }) {
  const set = (k) => (e) => onChange({ ...t, [k]: e.target.value });
  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid={`template-${kind}`}>
      <div className="flex items-center justify-between">
        <p className="font-medium">{NAMES[kind]}</p>
        <Button size="sm" variant="ghost" onClick={() => onChange(def)} data-testid={`template-${kind}-default`}><RotateCcw size={13} /> Default wording</Button>
      </div>
      <div className="space-y-1"><Label className="text-xs">Subject</Label><Input value={t.subject} maxLength={200} onChange={set("subject")} data-testid={`template-${kind}-subject`} /></div>
      <div className="space-y-1"><Label className="text-xs">Opening (before the invoice table)</Label><Textarea rows={4} value={t.intro} maxLength={4000} onChange={set("intro")} data-testid={`template-${kind}-intro`} /></div>
      <div className="space-y-1"><Label className="text-xs">Closing (after the invoice table)</Label><Textarea rows={3} value={t.closing} maxLength={4000} onChange={set("closing")} data-testid={`template-${kind}-closing`} /></div>
      <div className="rounded-md bg-white text-black p-3 text-sm space-y-2" data-testid={`template-${kind}-preview`}>
        <p className="text-xs text-slate-500">Preview · Subject: <b>{fill(t.subject)}</b></p>
        {fill(t.intro).split(/\n\s*\n/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
        <p className="text-xs text-slate-500 italic">[ table of open invoices and total ]</p>
        {fill(t.closing).split(/\n\s*\n/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
      </div>
    </div>
  );
}

export default function EmailTemplatesCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/email-templates").then((r) => setData(r.data)).catch((e) => toast.error(formatError(e))); }, []);
  const save = async () => {
    setBusy(true);
    try { setData((await api.put("/email-templates", data.templates)).data); toast.success("Email wording saved"); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  if (!data) return <Loader2 className="animate-spin text-primary" />;
  return (
    <div className="border rounded-lg bg-card p-5 space-y-4" data-testid="email-templates-card">
      <div>
        <h2 className="font-display text-xl font-bold">Email wording</h2>
        <p className="text-sm text-muted-foreground mt-1">Use these words in curly brackets and they're filled in for each customer:{" "}
          {data.placeholders.map((p) => <code key={p} className="mx-0.5 rounded bg-secondary px-1 text-xs">{`{${p}}`}</code>)}. Leave a blank line to start a new paragraph.</p>
      </div>
      <div className="grid xl:grid-cols-2 gap-4">
        {["statement", "reminder"].map((k) => (
          <TemplateEditor key={k} kind={k} t={data.templates[k]} def={data.defaults[k]} onChange={(t) => setData({ ...data, templates: { ...data.templates, [k]: t } })} />
        ))}
      </div>
      <Button onClick={save} disabled={busy} data-testid="email-templates-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save email wording</Button>
    </div>
  );
}
